import { useCallback, useEffect, useState } from 'react';
import { Button, Input, message, Radio, Select, Spin } from 'antd';
import { useClients } from '../../../context/ClientContext';
import {
  getUploadsByEnvironment,
  getZipStructure,
} from '../../../infrastructure/apiClient';
import type { ClientEnvironmentDTO, EnvironmentUploadDTO, BulkComparePayload } from '../../../infrastructure/models';
import styles from './ConfigurationComparison.module.scss';

const { TextArea } = Input;

export type ConfigurationComparisonPayload = BulkComparePayload & {
  comparisonName: string;
  comparisonDescription: string;
};

interface ConfigurationComparisonProps {
  onCompare?: (payload: ConfigurationComparisonPayload) => Promise<void> | void;
  onCancel?: () => void;
  environments?: ClientEnvironmentDTO[];
  environmentsLoading?: boolean;
}

const ConfigurationComparison = ({ onCompare, onCancel, environments = [], environmentsLoading = false }: ConfigurationComparisonProps) => {
  const { activeClient } = useClients();

  // Form fields
  const [comparisonName, setComparisonName] = useState('');
  const [comparisonDescription, setComparisonDescription] = useState('');

  // Source
  const [sourceEnvironmentId, setSourceEnvironmentId] = useState<string>('');
  const [sourceFiles, setSourceFiles] = useState<EnvironmentUploadDTO[]>([]);
  const [sourceFilesLoading, setSourceFilesLoading] = useState(false);
  const [sourceFileName, setSourceFileName] = useState<string>('');

  // Target
  const [targetEnvironmentId, setTargetEnvironmentId] = useState<string>('');
  const [targetFiles, setTargetFiles] = useState<EnvironmentUploadDTO[]>([]);
  const [targetFilesLoading, setTargetFilesLoading] = useState(false);
  const [targetFileName, setTargetFileName] = useState<string>('');

  // Folders (common)
  const [comparisonMode, setComparisonMode] = useState<'AllFolders' | 'SelectedFolders'>('AllFolders');
  const [folders, setFolders] = useState<string[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);

  const [submitting, setSubmitting] = useState(false);

  // Fetch files when source environment changes
  const fetchFilesForEnvironment = useCallback(
    async (tenantId: string, environmentId: string, signal?: AbortSignal): Promise<EnvironmentUploadDTO[]> => {
      const files = await getUploadsByEnvironment(tenantId, environmentId, signal);
      return files ?? [];
    },
    [],
  );

  useEffect(() => {
    if (!activeClient?.id || !sourceEnvironmentId) {
      setSourceFiles([]);
      setSourceFileName('');
      return;
    }
    const controller = new AbortController();
    setSourceFilesLoading(true);
    fetchFilesForEnvironment(activeClient.id, sourceEnvironmentId, controller.signal)
      .then((files) => {
        if (!controller.signal.aborted) {
          setSourceFiles(files);
          setSourceFileName('');
        }
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          console.error('Failed to load source files', err);
          setSourceFiles([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setSourceFilesLoading(false);
        }
      });
    return () => controller.abort();
  }, [activeClient?.id, sourceEnvironmentId, fetchFilesForEnvironment]);

  // Fetch files when target environment changes
  useEffect(() => {
    if (!activeClient?.id || !targetEnvironmentId) {
      setTargetFiles([]);
      setTargetFileName('');
      return;
    }
    const controller = new AbortController();
    setTargetFilesLoading(true);
    fetchFilesForEnvironment(activeClient.id, targetEnvironmentId, controller.signal)
      .then((files) => {
        if (!controller.signal.aborted) {
          setTargetFiles(files);
          setTargetFileName('');
        }
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          console.error('Failed to load target files', err);
          setTargetFiles([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setTargetFilesLoading(false);
        }
      });
    return () => controller.abort();
  }, [activeClient?.id, targetEnvironmentId, fetchFilesForEnvironment]);

  // Fetch zip structure for both source and target, then compute common folders
  useEffect(() => {
    // Only fetch in SelectedFolders mode
    if (comparisonMode !== 'SelectedFolders') {
      setFolders([]);
      setSelectedFolders([]);
      return;
    }
    if (!activeClient?.id) return;
    if (!sourceEnvironmentId || !sourceFileName || !targetEnvironmentId || !targetFileName) {
      setFolders([]);
      setSelectedFolders([]);
      return;
    }

    const controller = new AbortController();
    setFoldersLoading(true);

    Promise.all([
      getZipStructure(activeClient.id, sourceEnvironmentId, sourceFileName, controller.signal),
      getZipStructure(activeClient.id, targetEnvironmentId, targetFileName, controller.signal),
    ])
      .then(([sourceResult, targetResult]) => {
        if (controller.signal.aborted) return;
        const sourceFolderNames = new Set(
          (sourceResult?.folders ?? []).map((f) => f.folderName),
        );
        const commonFolders = (targetResult?.folders ?? [])
          .map((f) => f.folderName)
          .filter((name) => sourceFolderNames.has(name));
        setFolders(commonFolders);
        setSelectedFolders(commonFolders);
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          console.error('Failed to load zip structure', err);
          setFolders([]);
          setSelectedFolders([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setFoldersLoading(false);
        }
      });

    return () => controller.abort();
  }, [activeClient?.id, sourceEnvironmentId, targetEnvironmentId, sourceFileName, targetFileName, comparisonMode]);

  const handleSelectAllFolders = useCallback(
    (checked: boolean) => {
      setSelectedFolders(checked ? [...folders] : []);
    },
    [folders],
  );

  const handleCompare = async () => {
    if (!comparisonName.trim()) {
      message.warning('Enter a comparison name.');
      return;
    }
    if (!sourceEnvironmentId) {
      message.warning('Select a source environment.');
      return;
    }
    if (!sourceFileName) {
      message.warning('Select a source file.');
      return;
    }
    if (!targetEnvironmentId) {
      message.warning('Select a target environment.');
      return;
    }
    if (!targetFileName) {
      message.warning('Select a target file.');
      return;
    }
    if (comparisonMode === 'SelectedFolders' && selectedFolders.length === 0) {
      message.warning('Select at least one folder.');
      return;
    }

    const payload: ConfigurationComparisonPayload = {
      comparisonName: comparisonName.trim(),
      comparisonDescription: comparisonDescription.trim(),
      name: comparisonName.trim(),
      description: comparisonDescription.trim(),
      environmentA: sourceEnvironmentId,
      fileA: sourceFileName,
      environmentB: targetEnvironmentId,
      fileB: targetFileName,
      mode: comparisonMode,
      selections: comparisonMode === 'AllFolders' ? [] : selectedFolders,
    };

    try {
      setSubmitting(true);
      await onCompare?.(payload);
    } catch {
      message.error('Failed to create comparison.');
    } finally {
      setSubmitting(false);
    }
  };

  const allSelected = folders.length > 0 && selectedFolders.length === folders.length;

  return (
    <div className={styles.container} data-testid="configuration-comparison">
      {/* Name & Description */}
      <div className={styles.fieldGroup}>
        <div className={styles.field}>
          <label className={styles.label}>
            Comparison Name<span className={styles.required}>*</span>
          </label>
          <Input
            placeholder="Enter comparison name"
            value={comparisonName}
            onChange={(e) => setComparisonName(e.target.value)}
            disabled={submitting}
            className={styles.input}
            maxLength={200}
            data-testid="comparison-name-input"
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Comparison Description</label>
          <TextArea
            placeholder="Enter comparison description"
            value={comparisonDescription}
            onChange={(e) => setComparisonDescription(e.target.value)}
            disabled={submitting}
            className={styles.textArea}
            rows={3}
            maxLength={1000}
            data-testid="comparison-description-input"
          />
        </div>
      </div>

      {/* Source & Target side-by-side */}
      <div className={styles.sourceTargetRow}>
        {/* Source Section */}
        <div className={styles.sourceTargetColumn}>
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Source</h3>
            <div className={styles.field}>
              <label className={styles.label}>
                Environment<span className={styles.required}>*</span>
              </label>
              <Select
                placeholder="Select source environment"
                value={sourceEnvironmentId || undefined}
                onChange={(value) => setSourceEnvironmentId(value ?? '')}
                disabled={submitting}
                loading={environmentsLoading}
                allowClear
                className={styles.select}
                options={environments.map((env) => ({
                  value: env.id,
                  label: env.name,
                }))}
                data-testid="source-environment-select"
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>
                File Name<span className={styles.required}>*</span>
              </label>
              <Select
                placeholder="Select source file"
                value={sourceFileName || undefined}
                onChange={(value) => setSourceFileName(value ?? '')}
                disabled={submitting || !sourceEnvironmentId}
                loading={sourceFilesLoading}
                allowClear
                className={styles.select}
                options={sourceFiles.map((f) => ({
                  value: f.blobName,
                  label: f.originalFileName,
                }))}
                data-testid="source-file-select"
              />
            </div>
          </div>
        </div>

        {/* Target Section */}
        <div className={styles.sourceTargetColumn}>
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Target</h3>
            <div className={styles.field}>
              <label className={styles.label}>
                Environment<span className={styles.required}>*</span>
              </label>
              <Select
                placeholder="Select target environment"
                value={targetEnvironmentId || undefined}
                onChange={(value) => setTargetEnvironmentId(value ?? '')}
                disabled={submitting}
                loading={environmentsLoading}
                allowClear
                className={styles.select}
                options={environments.map((env) => ({
                  value: env.id,
                  label: env.name,
                }))}
                data-testid="target-environment-select"
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>
                File Name<span className={styles.required}>*</span>
              </label>
              <Select
                placeholder="Select target file"
                value={targetFileName || undefined}
                onChange={(value) => setTargetFileName(value ?? '')}
                disabled={submitting || !targetEnvironmentId}
                loading={targetFilesLoading}
                allowClear
                className={styles.select}
                options={targetFiles.map((f) => ({
                  value: f.blobName,
                  label: f.originalFileName,
                }))
                }
                data-testid="target-file-select"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Type */}
      <div className={styles.fieldGroup}>
        <div className={styles.field}>
          <label className={styles.label}>Comparison Type</label>
          <Radio.Group
            value={comparisonMode}
            onChange={(e) => setComparisonMode(e.target.value)}
            disabled={submitting}
            data-testid="comparison-mode-radio"
          >
            <Radio value="AllFolders">All Folders</Radio>
            <Radio value="SelectedFolders">Selected Folders</Radio>
          </Radio.Group>
        </div>
      </div>

      {/* Folders (shown only for SelectedFolders mode) */}
      {comparisonMode === 'SelectedFolders' && (
        <div className={styles.foldersSection}>
          {foldersLoading ? (
            <Spin data-testid="folders-loading" />
          ) : folders.length > 0 ? (
            <>
              <div className={styles.foldersHeader}>
                <label className={styles.label}>
                  Folders<span className={styles.required}>*</span>
                </label>
              </div>
              <Select
                mode="multiple"
                placeholder="Select folders"
                value={selectedFolders}
                onChange={(values) => setSelectedFolders(values)}
                disabled={submitting}
                className={styles.select}
                maxTagCount="responsive"
                options={[
                  {
                    value: '__select_all__',
                    label: allSelected ? 'Deselect All' : 'Select All',
                  },
                  ...folders.map((folder) => ({
                    value: folder,
                    label: folder,
                  })),
                ]}
                onSelect={(value: string) => {
                  if (value === '__select_all__') {
                    handleSelectAllFolders(!allSelected);
                  }
                }}
                onDeselect={(value: string) => {
                  if (value === '__select_all__') {
                    handleSelectAllFolders(false);
                  }
                }}
                data-testid="folders-select"
              />
            </>
          ) : null}
        </div>
      )}

      {/* Actions */}
      <div className={styles.actions}>
        <Button
          onClick={onCancel}
          disabled={submitting}
          className={styles.cancelButton}
          data-testid="cancel-button"
        >
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleCompare}
          loading={submitting}
          disabled={submitting}
          className={styles.compareButton}
          data-testid="compare-button"
        >
          Compare
        </Button>
      </div>
    </div>
  );
};

export default ConfigurationComparison;
